import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ShieldCheck, Receipt, Landmark, FolderLock, LifeBuoy, Network, CalendarDays, Sparkles, ArrowRight, AlertTriangle, CheckCircle2, Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo, AQMark } from "@/components/app/Logo";
import { HeroScreenshot } from "@/components/app/HeroScreenshot";
import { RiskSection } from "@/components/app/RiskSection";
import { PricingPlans, IntervalToggle } from "@/components/app/PricingPlans";
import { EnterpriseDialog } from "@/components/app/EnterpriseDialog";
import { useTheme } from "@/components/app/AppLayout";
import { useAuth } from "@/context/AuthContext";

const MODULES = [
  { icon: ShieldCheck, title: "Compliance Engine", text: "Annual returns, provincial, federal and state filings tracked automatically with live status and score impact.", span: "md:col-span-2" },
  { icon: Receipt, title: "Receipt Intelligence", text: "Snap a photo. AI extracts vendor, date, amount, tax and currency, then waits for your review." },
  { icon: Landmark, title: "Tax Tracker", text: "T2, GST/HST, payroll, federal, state and franchise taxes with late-filing risk." },
  { icon: FolderLock, title: "Corporate Records Vault", text: "Articles, by-laws, resolutions and minute books with versions, tags and AI search." },
  { icon: LifeBuoy, title: "Revival Workflows", text: "Eligibility, checklists, progress and generated revival packages for dissolved entities." },
  { icon: Network, title: "Multi-Entity Portfolio", text: "Holding structures with individual scores, a group health score and portfolio risk.", span: "md:col-span-2" },
  { icon: CalendarDays, title: "Filing Calendar", text: "Month, week and agenda views of every corporate, tax and revival deadline." },
  { icon: Sparkles, title: "AI Corporate Assistant", text: "Ask why your score is dropping and get a prioritized action list in seconds." },
];

const INSTANT = [
  ["Compliance status", "4 of 5 entities in good standing", CheckCircle2, "text-emerald-500"],
  ["Tax status", "2 returns overdue · GST/HST due in 9 days", Clock, "text-amber-500"],
  ["Missing filings", "Ontario Annual Return 12 days late", AlertTriangle, "text-orange-500"],
  ["Dissolution risk", "Harbourview Hospitality — dissolved", AlertTriangle, "text-red-500"],
  ["Revival requirements", "10-step checklist · eligible to restore", LifeBuoy, "text-violet-400"],
  ["Uncategorized receipts", "3 awaiting review", Receipt, "text-blue-400"],
];

export default function Landing() {
  const [interval, setInterval] = useState("monthly");
  const [ent, setEnt] = useState(false);
  const [dark, setDark] = useTheme();
  const { user } = useAuth();
  const nav = useNavigate();
  const choose = (k) => (k === "enterprise" ? setEnt(true) : nav(user ? "/app/billing" : `/register?plan=${k}&interval=${interval}`));
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b border-border bg-background/75 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-6">
          <Logo tagline />
          <nav className="hidden md:flex gap-6 text-sm text-muted-foreground">
            <a href="#modules" className="hover:text-foreground" data-testid="nav-link-modules">Platform</a>
            <a href="#score" className="hover:text-foreground" data-testid="nav-link-score">Health Score</a>
            <a href="#pricing" className="hover:text-foreground" data-testid="nav-link-pricing">Pricing</a>
          </nav>
          <div className="flex-1" />
          <button onClick={() => setDark(!dark)} className="text-xs text-muted-foreground hover:text-foreground" data-testid="landing-theme-toggle">{dark ? "Light" : "Dark"}</button>
          {user ? (
            <Button asChild size="sm" data-testid="go-to-app-btn"><Link to="/app">Open dashboard</Link></Button>
          ) : (
            <>
              <Link to="/login" className="text-sm hidden sm:block" data-testid="landing-signin-link">Sign in</Link>
              <Button asChild size="sm" data-testid="landing-start-trial-btn"><Link to="/register">Start free trial</Link></Button>
            </>
          )}
        </div>
      </header>

      <section className="relative overflow-hidden bg-[#0B0F19] text-slate-100 noise">
        <img src="/img/hero-tower.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-[0.13]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0F19] via-[#0B0F19]/90 to-[#0B0F19]/60" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-[0.85fr_1.15fr] gap-12 lg:gap-14 items-center">
          <div className="rise">
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-xs text-emerald-300 mb-6">
              <AQMark size={16} tile={false} testId="hero-mark" /> Corporate Health Operating System
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.04]">
              Never lose control of your corporation to a missed filing.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-300 max-w-xl leading-relaxed">
              Track compliance, manage filings, organize receipts, and monitor corporate health across every company you own.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="hero-start-trial-btn">
                <Link to="/register">Start 14-day free trial <ArrowRight className="h-4 w-4 ml-1" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white" data-testid="hero-pricing-btn">
                <a href="#pricing">See pricing</a>
              </Button>
            </div>
            <div className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-xs text-slate-400">
              {["Federal · ON · AB · BC", "Delaware · Michigan · Florida · Texas", "CRA & IRS deadlines"].map((t) => <span key={t}>{t}</span>)}
            </div>
          </div>
          <HeroScreenshot />
        </div>
      </section>

      <RiskSection />

      <section id="score" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-12">
          <div>
            <div className="eyebrow mb-3">The centerpiece</div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">One score. Six answers. Zero surprises.</h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">The Corporate Health Score (0–100) weighs compliance, tax, governance and record keeping for each entity and your whole group.</p>
            <div className="mt-8 space-y-2">
              {[["90–100", "Excellent", "#10B981"], ["75–89", "Healthy", "#3B82F6"], ["50–74", "Needs Attention", "#F59E0B"], ["25–49", "High Risk", "#F97316"], ["0–24", "Critical", "#EF4444"]].map(([r, l, c]) => (
                <div key={l} className="flex items-center gap-3 text-sm"><span className="h-2.5 w-8 rounded-full" style={{ background: c }} /><span className="font-mono w-16 text-muted-foreground">{r}</span><span className="font-medium">{l}</span></div>
              ))}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {INSTANT.map(([t, d, Icon, cls], i) => (
              <div key={t} className="surface p-5 lift rise" style={{ animationDelay: `${i * 70}ms` }}>
                <Icon className={`h-5 w-5 ${cls}`} />
                <div className="mt-3 font-semibold">{t}</div>
                <div className="text-sm text-muted-foreground mt-1">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="modules" className="border-y border-border bg-card/40 grid-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
          <div className="eyebrow mb-3">Platform</div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight max-w-2xl">HubSpot meets QuickBooks meets corporate compliance.</h2>
          <div className="mt-12 grid md:grid-cols-4 gap-4">
            {MODULES.map((m) => (
              <div key={m.title} className={`surface p-6 lift ${m.span || ""}`} data-testid={`module-card-${m.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><m.icon className="h-5 w-5" /></div>
                <h3 className="mt-4 font-semibold text-lg">{m.title}</h3>
                <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{m.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="pricing" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="eyebrow mb-3">Pricing</div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Cheaper than a single late-filing penalty.</h2>
          </div>
          <IntervalToggle interval={interval} setInterval={setInterval} />
        </div>
        <PricingPlans interval={interval} onSelect={choose} />
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="relative overflow-hidden rounded-2xl bg-[#0B1B3A] text-white p-10 sm:p-14 noise">
          <h2 className="text-3xl sm:text-4xl font-bold max-w-2xl">No business owner should lose control of their corporation.</h2>
          <p className="mt-3 text-slate-300 max-w-xl">Load a demo portfolio in one click and see your group's health in under a minute.</p>
          <Button asChild size="lg" className="mt-8 bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="cta-start-trial-btn"><Link to="/register">Start free trial</Link></Button>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col sm:flex-row gap-4 justify-between text-sm text-muted-foreground">
          <Logo tagline />
          <p>© {new Date().getFullYear()} AccruallQ. Deadlines are estimates — confirm with your registry or advisor.</p>
        </div>
      </footer>
      <EnterpriseDialog open={ent} onOpenChange={setEnt} />
    </div>
  );
}
